import type { PlanSet } from '../types/planSets';
import type { PricingExperiment } from '../interfaces/planSet.interface';
import { COURSE_PLAN } from '../../shared/utils/SubscriptionPlans';
import { EXPERIMENTS } from '../../shared/utils/experiments';
import { PLAN_SET, PLAN_SETS } from '../common/constants/planSets';
import { PLAN_SET_QUERY_KEY } from '../common/constants/planSetQuery';
import { resolvePlanSet } from '../shared/utils/planSets';
import { useUSPricing } from './useUSPricing';
import { useUpsellExperiment } from './useUpsellExperiment';

const FUNNEL_PLAN_SETS: Record<string, PlanSet> = {
  'email-landing': PLAN_SET.EMAIL_LANDING,
  'pricing-11': PLAN_SET.PRICING_11,
  'pricing-12': PLAN_SET.PRICING_12,
  'pricing-13': PLAN_SET.PRICING_13,
  'pricing-14': PLAN_SET.PRICING_14,
  'pricing-15': PLAN_SET.PRICING_15,
  'pricing-21': PLAN_SET.PRICING_21,
  'pricing-22': PLAN_SET.PRICING_22,
  'pricing-23': PLAN_SET.PRICING_23,
  'pricing-24': PLAN_SET.PRICING_24,
  'pricing-25': PLAN_SET.PRICING_25,
  'pricing-31': PLAN_SET.PRICING_31,
  'pricing-32': PLAN_SET.PRICING_32,
  'pricing-33': PLAN_SET.PRICING_33,
  'pricing-34': PLAN_SET.PRICING_34,
  'pricing-35': PLAN_SET.PRICING_35,
};

export function usePaymentConfigs(locale: Ref<string>) {
  type LangKeys = 'en' | 'pt' | 'es-mx' | 'pt-br' | 'es' | 'de';

  const appConfig = useAppConfig();
  const route = useRoute();
  const { isUSPricing } = useUSPricing();
  const { getVariation } = useGrowthBook();
  const gatewayStore = useKegelGatewayStore();
  const { hasNewUpsellPlans } = useUpsellExperiment();

  const currentPlanSet = computed<PlanSet>(() => {
    const pricingExperiments: PricingExperiment[] = [
      { key: EXPERIMENTS.WW_PRICING_C, activeVariant: 'c', planSet: PLAN_SET.WW_PRICING_C },
      { key: EXPERIMENTS.LATAM_PRICING_C, activeVariant: 'c', planSet: PLAN_SET.WW_PRICING_C },
      { key: EXPERIMENTS.US_PRICING_TEST_2, activeVariant: '$19.99 → $34.99', planSet: PLAN_SET.US_PRICING_TEST_2_A },
      { key: EXPERIMENTS.US_PRICING_TEST_2, activeVariant: '$19.99 → $39.99', planSet: PLAN_SET.US_PRICING_TEST_2_B },
      { key: EXPERIMENTS.US_PRICING_TEST_2, activeVariant: '$22.99 → $39.99', planSet: PLAN_SET.US_PRICING_TEST_2_C },
      { key: EXPERIMENTS.US_PRICING_TEST_2, activeVariant: '$24.99 → $39.99', planSet: PLAN_SET.US_PRICING_TEST_2_D },
      { key: EXPERIMENTS.US_PRICING_TEST_2, activeVariant: '$29.99 → $59.99', planSet: PLAN_SET.US_PRICING_TEST_2_E },
    ];

    return resolvePlanSet({
      funnelPlanSet: FUNNEL_PLAN_SETS[String(appConfig.planSet ?? '')],
      purchasedPlan: route.meta.purchasedPlanSet
        ? { planSet: route.query[PLAN_SET_QUERY_KEY], planId: route.query.pID }
        : undefined,
      isRound: gatewayStore.provider === 'round',
      isUSPricing: !!appConfig.usPricing || isUSPricing.value,
      isInUpsellExperiment: () => hasNewUpsellPlans.value,
      pricingExperiments,
      readVariation: key => getVariation<string>(key, 'control'),
    });
  });

  return reactive({
    currentPlanSet,

    currentConfig: computed<SubscriptionModel[]>(() => {
      const subscriptions = PLAN_SETS[currentPlanSet.value];

      switch (locale.value as LangKeys) {
        case 'en':
        case 'pt':
        case 'es-mx':
        case 'pt-br':
        case 'es':
        case 'de':
        default:
          return subscriptions;
      }
    }),

    coursesConfig: computed<SubscriptionModel[]>(() => {
      switch (locale.value as LangKeys) {
        case 'en':
        case 'pt':
        case 'es-mx':
        case 'pt-br':
        case 'es':
        case 'de':
        default:
          return COURSE_PLAN;
      }
    }),
  });
}
